import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { contactApi, contactRoleApi, orgApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CUSTOMER_ROLE_OPTIONS = [
  "artist_principal", "tour_manager", "production_manager", "business_manager",
  "personal_manager", "road_manager", "transportation_coordinator", "accountant",
  "accounts_payable", "booking_agent", "manager", "assistant", "security",
  "vendor_contact", "other",
];

/**
 * Simplified & reusable Contact form.
 * Minimum required: first_name + last_name.
 * Optionally attaches to a Customer via an Organization (defaultCustomer prop).
 * Optional role field defaults blank — user can save without picking a role.
 * NOT tied to a Client at creation (defaultCustomer is a convenience for
 * flows launched from Customer 360 / Tour Contacts).
 */
export const ContactFormDialog = ({
  open,
  editing,
  orgs: orgsProp,
  onClose,
  onSaved,
  defaultCustomer, // {id, display_organization_id, legal_entity_organization_id, display_organization, legal_entity_organization, ...}
  suggestedRole,   // optional string — pre-fill role selector when launched from a Tour slot
}) => {
  const [orgs, setOrgs] = useState(orgsProp || []);
  const [form, setForm] = useState({
    first_name: editing?.first_name || "",
    last_name: editing?.last_name || "",
    title: editing?.title || "",
    primary_email: editing?.primary_email || "",
    mobile: editing?.mobile || "",
    // More Details:
    preferred_name: editing?.preferred_name || "",
    secondary_email: editing?.secondary_email || "",
    office_phone: editing?.office_phone || "",
    alternate_phone: editing?.alternate_phone || "",
    preferred_contact_method: editing?.preferred_contact_method || "",
    notes: editing?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [duplicate, setDuplicate] = useState(null);  // primary legacy match (retained)
  const [dupCandidates, setDupCandidates] = useState([]);  // RC10 P2C
  const [confirmCreate, setConfirmCreate] = useState(false);
  // RC10 Phase 2A · Portal-safety notice. When editing a Contact
  // linked to an ACTIVE portal user, show the Owner-provided verbatim
  // notice explaining that changing the canonical email here does NOT
  // change the Portal login. Backend `GET /api/contacts/{id}` returns
  // `portal_link_status`; we fetch once on open.
  const [portalStatus, setPortalStatus] = useState(null);
  useEffect(() => {
    if (!editing?.id || !open) { setPortalStatus(null); return; }
    contactApi
      .get(editing.id)
      .then((c) => setPortalStatus(c?.portal_link_status || null))
      .catch(() => setPortalStatus(null));
  }, [editing?.id, open]);
  const showPortalSafetyNotice = Boolean(
    portalStatus?.is_linked && portalStatus?.portal_activation_state === "active"
  );

  // Attach-to-customer state (optional)
  const initialOrg =
    defaultCustomer?.legal_entity_organization_id ||
    defaultCustomer?.display_organization_id ||
    "";
  const [attachOrgId, setAttachOrgId] = useState(initialOrg);
  const [attachRole, setAttachRole] = useState(suggestedRole || "");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!orgsProp) {
      orgApi
        .list({ page_size: 200 })
        .then((d) => setOrgs(d.results || []))
        .catch(() => setOrgs([]));
    }
  }, [orgsProp]);

  // RC10 Phase 2C · Debounced duplicate check via the canonical
  // /api/contacts/dup-check endpoint (exact-email OR name+phone;
  // tenant-scoped when `defaultCustomer.id` is present).
  useEffect(() => {
    if (editing?.id) return;
    const name = `${form.first_name} ${form.last_name}`.trim();
    const email = (form.primary_email || "").trim();
    const phone = (form.mobile || "").trim();
    // At least email OR (name AND phone) must be present to check.
    const hasEmail = email.length > 3;
    const hasNamePhone = name.length >= 3 && phone.length >= 6;
    if (!hasEmail && !hasNamePhone) {
      setDuplicate(null);
      setDupCandidates([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await contactApi.dupCheck({
          email: hasEmail ? email : undefined,
          name: hasNamePhone ? name : undefined,
          phone: hasNamePhone ? phone : undefined,
          client_id: defaultCustomer?.id,
        });
        const results = res.results || [];
        setDupCandidates(results);
        setDuplicate(results.find((r) => !r.is_archived) || results[0] || null);
      } catch {
        setDuplicate(null);
        setDupCandidates([]);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.first_name, form.last_name, form.primary_email, form.mobile]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      return toast.error("First and last name are required");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(
        (k) => payload[k] === "" && (payload[k] = null)
      );
      let contact;
      if (editing?.id) {
        contact = await contactApi.patch(editing.id, payload);
      } else {
        contact = await contactApi.create(payload);
      }
      // Optional attach to organization (customer surrogate) with role
      if (!editing?.id && attachOrgId && attachRole) {
        try {
          await contactRoleApi.attach({
            contact_id: contact.id,
            organization_id: attachOrgId,
            role: attachRole,
          });
        } catch (err) {
          const msg = apiError(err);
          if (!/already/i.test(msg)) toast.error(msg);
        }
      }
      toast.success(editing?.id ? "Contact updated" : "Contact created");
      onSaved(contact);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const customerOrgs = useMemo(() => {
    if (!defaultCustomer) return [];
    return [
      defaultCustomer.display_organization,
      defaultCustomer.legal_entity_organization,
      defaultCustomer.billing_organization,
    ].filter(Boolean);
  }, [defaultCustomer]);

  // F2 · Attach-Contact dropdown. Owner rule M defect: the previous
  // implementation showed ONLY the three organizations already
  // slotted on the passed defaultCustomer (display / legal / billing),
  // which is empty until at least one FK is populated. Merge those
  // pre-slotted orgs with the loaded active organization list (from
  // GET /api/organizations, populated by state `orgs`), dedup by id,
  // and preserve ordering so the customer-linked orgs float to the
  // top when present.
  const attachOrgChoices = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const o of customerOrgs) {
      if (o && !seen.has(o.id)) {
        seen.add(o.id);
        out.push(o);
      }
    }
    for (const o of orgs || []) {
      if (o && !seen.has(o.id) && !o.is_archived) {
        seen.add(o.id);
        out.push(o);
      }
    }
    return out;
  }, [customerOrgs, orgs]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col"
        data-testid="contact-form-dialog"
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-display">
            {editing ? "Edit Contact" : "New Contact"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-[13px] px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-2 gap-2">
            <Input
              label="First Name"
              required
              autoFocus
              value={form.first_name}
              onChange={(v) => setField("first_name", v)}
              testid="cf2-first"
            />
            <Input
              label="Last Name"
              required
              value={form.last_name}
              onChange={(v) => setField("last_name", v)}
              testid="cf2-last"
            />
          </div>

          {!editing && dupCandidates.length > 0 && (
            <div
              data-testid="cf2-duplicate-warning"
              className="border border-amber-hold bg-amber-hold/15 rounded-sm p-3 flex gap-2"
            >
              <AlertTriangle
                size={16}
                className="text-amber-hold shrink-0 mt-0.5"
              />
              <div className="flex-1 text-[12.5px] min-w-0">
                <div className="font-medium text-carbon">
                  Possible existing contact{dupCandidates.length > 1 ? "s" : ""} found:
                </div>
                <ul
                  data-testid="cf2-duplicate-candidates"
                  className="mt-1 space-y-1"
                >
                  {dupCandidates.slice(0, 10).map((cand) => (
                    <li
                      key={cand.contact_id}
                      data-testid={`cf2-duplicate-candidate-${cand.contact_id}`}
                      className="flex flex-wrap items-center gap-2 min-h-[44px] py-1"
                    >
                      <span className="truncate">
                        {cand.display_name}
                        {cand.primary_email ? ` · ${cand.primary_email}` : ""}
                        {cand.primary_phone ? ` · ${cand.primary_phone}` : ""}
                      </span>
                      {cand.is_archived && (
                        <span
                          className="text-[10px] uppercase tracking-widest text-amber-hold border border-amber-hold/40 rounded-sm px-1.5 py-0.5"
                          data-testid={`cf2-duplicate-archived-${cand.contact_id}`}
                        >
                          Archived · not linkable
                        </span>
                      )}
                      {!cand.is_archived && (
                        <button
                          type="button"
                          data-testid={`cf2-duplicate-link-${cand.contact_id}`}
                          onClick={() => onSaved({
                            id: cand.contact_id,
                            first_name: cand.display_name,
                            primary_email: cand.primary_email,
                            mobile: cand.primary_phone,
                            _link_existing: true,
                          })}
                          className="text-coach-blue hover:underline min-h-[44px] px-2"
                        >
                          Link Existing Contact
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex gap-3">
                  <button
                    type="button"
                    data-testid="cf2-duplicate-create-anyway"
                    onClick={() => setConfirmCreate(true)}
                    className="text-steel hover:text-carbon min-h-[44px]"
                  >
                    Create Separate Contact Anyway…
                  </button>
                </div>
              </div>
            </div>
          )}
          {confirmCreate && (
            <div
              role="alertdialog"
              data-testid="cf2-duplicate-create-anyway-confirm"
              className="border border-critical-red/60 bg-critical-red/10 rounded-sm p-3 space-y-2"
            >
              <div className="text-[12.5px] font-medium text-carbon">
                Confirm: create a separate Contact record even though a possible match exists?
              </div>
              <div className="text-[11.5px] text-steel">
                This does NOT merge or overwrite the existing Contact.
                The two Contacts will remain distinct records.
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="cf2-duplicate-create-anyway-yes"
                  onClick={() => { setDuplicate(null); setDupCandidates([]); setConfirmCreate(false); }}
                  className="text-critical-red hover:underline min-h-[44px] px-2"
                >
                  Yes, create separate Contact
                </button>
                <button
                  type="button"
                  data-testid="cf2-duplicate-create-anyway-cancel"
                  onClick={() => setConfirmCreate(false)}
                  className="text-steel hover:text-carbon min-h-[44px] px-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Input
              label="Phone (Mobile)"
              value={form.mobile}
              onChange={(v) => setField("mobile", v)}
              testid="cf2-mobile"
            />
            <Input
              label="Email"
              type="email"
              value={form.primary_email}
              onChange={(v) => setField("primary_email", v)}
              testid="cf2-email"
            />
          </div>
          {showPortalSafetyNotice && (
            <div
              data-testid="cf2-portal-safety-notice"
              className="border border-amber-hold/70 bg-amber-hold/10 text-carbon text-[12.5px] rounded-sm px-3 py-2"
            >
              Contact information was updated. This person's Client Portal login email has not changed. Portal login changes must be completed separately through Portal Access.
            </div>
          )}
          <Input
            label="Title"
            value={form.title}
            onChange={(v) => setField("title", v)}
            testid="cf2-title"
          />

          {/* Optional attach-to-customer */}
          {!editing && defaultCustomer && (
            <div
              data-testid="cf2-attach-customer-block"
              className="border border-coach-silver rounded-sm p-3 bg-road-white"
            >
              <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-2">
                Attach to {defaultCustomer.display_name}{" "}
                <span className="text-steel/70 normal-case">
                  (optional)
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <div className="text-[11px] uppercase tracking-wide text-steel mb-1">
                    Organization
                  </div>
                  <select
                    data-testid="cf2-attach-org"
                    value={attachOrgId}
                    onChange={(e) => setAttachOrgId(e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white"
                  >
                    <option value="">— Choose —</option>
                    {attachOrgChoices.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.display_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <div className="text-[11px] uppercase tracking-wide text-steel mb-1">
                    Role (optional)
                  </div>
                  <select
                    data-testid="cf2-attach-role"
                    value={attachRole}
                    onChange={(e) => setAttachRole(e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white"
                  >
                    <option value="">— No role —</option>
                    {CUSTOMER_ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="text-[11px] text-steel mt-2">
                Both fields optional. Leave role blank if unknown — you can add
                it later.
              </div>
            </div>
          )}

          <button
            type="button"
            data-testid="cf2-more-toggle"
            onClick={() => setMoreOpen((v) => !v)}
            className="flex items-center gap-1 text-[12px] text-coach-blue hover:underline"
          >
            {moreOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            More Details {moreOpen ? "" : "(preferred name, alt contacts, notes)"}
          </button>

          {moreOpen && (
            <div
              data-testid="cf2-more-details"
              className="border-l-2 border-coach-silver pl-3 space-y-2"
            >
              <Input
                label="Preferred Name"
                value={form.preferred_name}
                onChange={(v) => setField("preferred_name", v)}
                testid="cf2-pref"
              />
              <Input
                label="Secondary Email"
                type="email"
                value={form.secondary_email}
                onChange={(v) => setField("secondary_email", v)}
                testid="cf2-secondary-email"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Office Phone"
                  value={form.office_phone}
                  onChange={(v) => setField("office_phone", v)}
                  testid="cf2-office"
                />
                <Input
                  label="Alternate Phone"
                  value={form.alternate_phone}
                  onChange={(v) => setField("alternate_phone", v)}
                  testid="cf2-altphone"
                />
              </div>
              <label className="block">
                <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">
                  Preferred Contact Method
                </div>
                <select
                  data-testid="cf2-pref-method"
                  value={form.preferred_contact_method}
                  onChange={(e) =>
                    setField("preferred_contact_method", e.target.value)
                  }
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
                >
                  <option value="">— No preference —</option>
                  {["email", "mobile", "office", "other"].map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">
                  Notes
                </div>
                <textarea
                  data-testid="cf2-notes"
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                  rows={2}
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                />
              </label>
            </div>
          )}

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-[12.5px] border border-coach-silver rounded-sm text-steel"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="cf2-submit"
              disabled={saving}
              className="px-4 py-1.5 text-[12.5px] font-medium bg-coach-blue text-white rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Input = ({ label, value, onChange, type = "text", required, testid, autoFocus }) => (
  <label className="block">
    <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">
      {label} {required && <span className="text-critical-red">*</span>}
    </div>
    <input
      type={type}
      required={required}
      autoFocus={autoFocus}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
    />
  </label>
);
